import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/store/authStore";
import axios from "axios";
import { env } from "@/config/env";
import { logger } from "@/utils/logger";
import { Input, Button } from "@biartbt/biartd";

const SignInForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      logger.info("Sign in attempt", { email });
      
      const response = await axios.post(`${env.authApiUrl}/users/signin`, {
        email,
        password,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      logger.info("Sign in successful", { userId: response.data?.user?.id });
      const token = response.data.token ?? response.data.access_token;
      if (token && response.data.user) {
        login(response.data.user, token);
        navigate("/dashboard");
      }
    } catch (err: any) {
      // Extract error message from response
      let errorMessage = "Kullanıcı adı veya şifre hatalı olabilir. Lütfen tekrar deneyin.";
      
      if (err.response?.data) {
        // Try to get message from JSON response
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.errorMessage) {
          errorMessage = err.response.data.errorMessage;
        }
      } else if (err.message && !err.message.includes('Network Error')) {
        // Only use err.message if it's not a network error
        errorMessage = err.message;
      }
      
      // Log error to console for debugging (but don't show technical details to user)
      logger.error("Sign in failed", err, {
        url: `${env.authApiUrl}/users/signin`,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        request: { email },
      });
      
      // Show user-friendly error message
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="auth-main-content bg-white dark:bg-[#0a0e19] py-[60px] md:py-[80px] lg:py-[135px]">
        <div className="mx-auto px-[12.5px] md:max-w-[720px] lg:max-w-[960px] xl:max-w-[1255px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-[25px] items-center">
            <div className="xl:ltr:-mr-[25px] xl:rtl:-ml-[25px] 2xl:ltr:-mr-[45px] 2xl:rtl:-ml-[45px] rounded-[25px] order-2 lg:order-1">
              <img
                src="/images/sign-in.jpg"
                alt="sign-in-image"
                className="rounded-[25px]"
                width={646}
                height={804}
              />
            </div>

            <div className="xl:ltr:pl-[90px] xl:rtl:pr-[90px] 2xl:ltr:pl-[120px] 2xl:rtl:pr-[120px] order-1 lg:order-2">
              <img
                src="/images/logo-big.svg"
                alt="logo"
                className="inline-block dark:hidden"
                width={142}
                height={38}
              />
              <img
                src="/images/white-logo-big.svg"
                alt="logo"
                className="hidden dark:inline-block"
                width={142}
                height={38}
              />

              <div className="my-[17px] md:my-[25px]">
                <h1 className="!font-semibold !text-[22px] md:!text-xl lg:!text-2xl !mb-[5px] md:!mb-[7px]">
                  {t("auth.welcomeBack")}
                </h1>
                <p className="font-medium lg:text-md text-[#445164] dark:text-gray-400">
                  {t("auth.signInWithSocial")}
                </p>
              </div>

              {error && (
                <div className="mb-[15px] p-[12px] rounded-md bg-danger-50 dark:bg-danger-900 text-danger-600 dark:text-danger-400 border border-danger-200 dark:border-danger-700">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-between mb-[20px] md:mb-[23px] gap-[12px]">
                <div className="grow">
                  <button
                    type="button"
                    className="block text-center w-full rounded-md transition-all py-[8px] md:py-[10.5px] px-[15px] md:px-[25px] text-black dark:text-white border border-[#D6DAE1] bg-white dark:bg-[#0a0e19] dark:border-[#172036] shadow-sm hover:border-primary-500"
                  >
                    <img
                      src="/images/icons/google.svg"
                      className="inline-block"
                      alt="google"
                      width={25}
                      height={25}
                    />
                  </button>
                </div>

                <div className="grow">
                  <button
                    type="button"
                    className="block text-center w-full rounded-md transition-all py-[8px] md:py-[10.5px] px-[15px] md:px-[25px] text-black dark:text-white border border-[#D6DAE1] bg-white dark:bg-[#0a0e19] dark:border-[#172036] shadow-sm hover:border-primary-500"
                  >
                    <img
                      src="/images/icons/facebook2.svg"
                      className="inline-block"
                      alt="facebook"
                      width={25}
                      height={25}
                    />
                  </button>
                </div>

                <div className="grow">
                  <button
                    type="button"
                    className="block text-center w-full rounded-md transition-all py-[8px] md:py-[10.5px] px-[15px] md:px-[25px] text-black dark:text-white border border-[#D6DAE1] bg-white dark:bg-[#0a0e19] dark:border-[#172036] shadow-sm hover:border-primary-500"
                  >
                    <img
                      src="/images/icons/apple.svg"
                      className="inline-block"
                      alt="apple"
                      width={25}
                      height={25}
                    />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-[15px]">
                  <Input
                    type="email"
                    label={t("common.email")}
                    id="email"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    required
                    placeholder="example@trezo.com"
                    leftIcon={<i className="material-symbols-outlined text-lg">mail</i>}
                    error={!!error && error.includes("email")}
                  />
                </div>

                <div className="mb-[15px]" id="passwordHideShow">
                  <Input
                    type="password"
                    label={t("common.password")}
                    name="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    required
                    id="password"
                    placeholder="Type password"
                    leftIcon={<i className="material-symbols-outlined text-lg">lock</i>}
                    error={!!error && error.includes("password")}
                  />
                </div>

                <Link
                  to="/forgot-password"
                  className="inline-block text-primary-500 transition-all font-semibold hover:underline mb-[20px]"
                >
                  {t("common.forgotPassword")}?
                </Link>

                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  className="w-full mt-[20px] md:mt-[25px]"
                  loading={loading}
                  disabled={loading}
                  leftIcon={!loading && <i className="material-symbols-outlined">login</i>}
                >
                  {loading ? "Signing in..." : t("common.signIn")}
                </Button>

                <p className="mt-[15px] md:mt-[20px]">
                  {t("auth.dontHaveAccount")}{" "}
                  <Link
                    to="/sign-up"
                    className="text-primary-500 transition-all font-semibold hover:underline"
                  >
                    {t("common.signUp")}
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignInForm;
